/**
 * Agent/task color assignment for chat traces and the agent console.
 * One deterministic hash, one palette shape, light + dark variants — so the
 * same subagent renders with the same identity on every surface and mode.
 */

export type ColorMode = "light" | "dark";

export interface AgentColor {
  primary: string;
  soft: string;
  border: string;
  text: string;
}

// The 8 hues are shared between modes; soft/border are alpha tints of the
// hue, text is dark-shade (light mode) or a +55% tint (dark mode, all ≥8:1
// on navy900).
const AGENT_PALETTE_LIGHT: AgentColor[] = [
  { primary: "#0284c7", soft: "rgba(2, 132, 199, 0.08)", border: "rgba(2, 132, 199, 0.25)", text: "#0c4a6e" }, // sky
  { primary: "#ea580c", soft: "rgba(234, 88, 12, 0.08)", border: "rgba(234, 88, 12, 0.25)", text: "#7c2d12" }, // orange
  { primary: "#9333ea", soft: "rgba(147, 51, 234, 0.08)", border: "rgba(147, 51, 234, 0.25)", text: "#581c87" }, // purple
  { primary: "#0891b2", soft: "rgba(8, 145, 178, 0.08)", border: "rgba(8, 145, 178, 0.25)", text: "#164e63" }, // cyan
  { primary: "#d97706", soft: "rgba(217, 119, 6, 0.08)", border: "rgba(217, 119, 6, 0.25)", text: "#78350f" }, // amber
  { primary: "#059669", soft: "rgba(5, 150, 105, 0.08)", border: "rgba(5, 150, 105, 0.25)", text: "#064e3b" }, // emerald
  { primary: "#dc2626", soft: "rgba(220, 38, 38, 0.08)", border: "rgba(220, 38, 38, 0.25)", text: "#7f1d1d" }, // red
  { primary: "#2563eb", soft: "rgba(37, 99, 235, 0.08)", border: "rgba(37, 99, 235, 0.25)", text: "#1e3a8a" }, // blue
];

const AGENT_PALETTE_DARK: AgentColor[] = [
  { primary: "#0284c7", soft: "rgba(2, 132, 199, 0.16)", border: "rgba(2, 132, 199, 0.40)", text: "#8dc8e6" },
  { primary: "#ea580c", soft: "rgba(234, 88, 12, 0.16)", border: "rgba(234, 88, 12, 0.40)", text: "#f6b492" },
  { primary: "#9333ea", soft: "rgba(147, 51, 234, 0.16)", border: "rgba(147, 51, 234, 0.40)", text: "#cea3f6" },
  { primary: "#0891b2", soft: "rgba(8, 145, 178, 0.16)", border: "rgba(8, 145, 178, 0.40)", text: "#90cedc" },
  { primary: "#d97706", soft: "rgba(217, 119, 6, 0.16)", border: "rgba(217, 119, 6, 0.40)", text: "#eec28f" },
  { primary: "#059669", soft: "rgba(5, 150, 105, 0.16)", border: "rgba(5, 150, 105, 0.40)", text: "#8fd0bc" },
  { primary: "#dc2626", soft: "rgba(220, 38, 38, 0.16)", border: "rgba(220, 38, 38, 0.40)", text: "#ef9d9d" },
  { primary: "#2563eb", soft: "rgba(37, 99, 235, 0.16)", border: "rgba(37, 99, 235, 0.40)", text: "#9db9f6" },
];

export const AGENT_COLOR_PALETTES: Record<ColorMode, AgentColor[]> = {
  light: AGENT_PALETTE_LIGHT,
  dark: AGENT_PALETTE_DARK,
};

// Deterministic hash so a given agent name always maps to the same color.
export const hashString = (value: string): number => {
  let hash = 0;
  for (let i = 0; i < value.length; i++) {
    hash = (hash << 5) - hash + value.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
};

export const getAgentColor = (agent: string, mode: ColorMode = "light"): AgentColor => {
  const palette = AGENT_COLOR_PALETTES[mode];
  return palette[hashString(agent) % palette.length];
};

// User (officer) messages: the brand navy in light mode, the periwinkle
// accent family in dark mode.
const USER_COLOR_LIGHT: AgentColor = {
  primary: "#26293B",
  soft: "rgba(38, 41, 59, 0.08)",
  border: "rgba(38, 41, 59, 0.25)",
  text: "#26293B",
};
const USER_COLOR_DARK: AgentColor = {
  primary: "#9ca4d3",
  soft: "rgba(156, 164, 211, 0.12)",
  border: "rgba(156, 164, 211, 0.35)",
  text: "#bcc2e6",
};
export const getUserColor = (mode: ColorMode = "light"): AgentColor =>
  mode === "dark" ? USER_COLOR_DARK : USER_COLOR_LIGHT;

// Tool calls render with the amber identity.
export const getToolColor = (mode: ColorMode = "light"): AgentColor =>
  AGENT_COLOR_PALETTES[mode][4];

// Cycle palette for grouping subagent "tasks" inside a turn. Assigned by
// incrementing index (not hash) — order within a conversation matters more
// than cross-conversation stability here.
export const TASK_COLORS = [
  "#E57373", "#F06292", "#BA68C8", "#9575CD", "#7986CB",
  "#64B5F6", "#4FC3F7", "#4DD0E1", "#4DB6AC", "#81C784",
  "#AED581", "#DCE775", "#FFF176", "#FFD54F", "#FFB74D",
  "#FF8A65", "#A1887F", "#90A4AE", "#78909C", "#B0BEC5",
] as const;
